import { ErrorBoundary } from "react-error-boundary";
import { Text, Title } from "../../tremor/Text";
import { Button } from "../../tremor/Button";
import { TableChart } from "../TableChart";
import React, { useMemo, useRef, useState } from "react";
import "chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm";
import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, Colors } from "chart.js";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { MetricChart, MetricPlugin } from "../MetricChart";
import TextChart from "./TextChart";
import ImageChart from "./ImageChart";
import { DividerChart, DividerPlugin } from "./DividerChart";
import { FunnelController, TrapezoidElement, } from "chartjs-chart-funnel";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { PolygonController, PolylineController, MarkerController, MapPlugin, MapController } from "@onvo-ai/chartjs-chart-map";
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud';
import { SankeyController, Flow } from 'chartjs-chart-sankey';
import "chart.js/auto";
function transparentize(hexColor, opacity) {
    // Convert opacity to hex value
    let alphaHex = Math.round(opacity * 255).toString(16);
    if (alphaHex.length === 1) {
        alphaHex = "0" + alphaHex;
    }
    // Return the hex color with the alpha value
    return hexColor + alphaHex;
}
ChartJS.register(PolygonController, PolylineController, MarkerController, MapController, FunnelController, TrapezoidElement, MetricChart, MetricPlugin, TextChart, ImageChart, DividerPlugin, DividerChart, ChartDataLabels, zoomPlugin, WordCloudController, WordElement, SankeyController, Flow, MatrixController, MatrixElement, Colors);
const ChartBase = ({ json, title }) => {
    const chartRef = useRef();
    const [zoomed, setZoomed] = useState(false);
    const theme = useTheme();
    const resetZoom = () => {
        var _a;
        (_a = chartRef.current) === null || _a === void 0 ? void 0 : _a.resetZoom();
        setZoomed(false);
    };
    let chartConfig = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        if (!json) {
            return undefined;
        }
        let output = Object.assign({}, json, {});
        if (!output.options) {
            output.options = {};
        }
        if (!output.options.plugins) {
            output.options.plugins = {};
        }
        let subtitle = ((_b = (_a = output.options.plugins) === null || _a === void 0 ? void 0 : _a.subtitle) === null || _b === void 0 ? void 0 : _b.display) !== false &&
            ((_e = (_d = (_c = output.options.plugins) === null || _c === void 0 ? void 0 : _c.subtitle) === null || _d === void 0 ? void 0 : _d.text) === null || _e === void 0 ? void 0 : _e.trim) &&
            ((_h = (_g = (_f = output.options.plugins) === null || _f === void 0 ? void 0 : _f.subtitle) === null || _g === void 0 ? void 0 : _g.text) === null || _h === void 0 ? void 0 : _h.trim()) !== "";
        output.options.plugins.title = {
            display: (title || ((_j = output.options.plugins.title) === null || _j === void 0 ? void 0 : _j.text) || "").trim() === "" ? false : true,
            text: title || ((_k = output.options.plugins.title) === null || _k === void 0 ? void 0 : _k.text) || "",
            align: ((_l = output.options.plugins.title) === null || _l === void 0 ? void 0 : _l.align) || "start",
            position: ((_m = output.options.plugins.title) === null || _m === void 0 ? void 0 : _m.position) || "top",
            fullSize: true,
            font: {
                size: output.type === "text" ? 24 : 18,
                weight: 600,
            },
            color: theme === "dark" ? "#ddd" : "#111",
            padding: ((_o = output.options.plugins.title) === null || _o === void 0 ? void 0 : _o.padding) || {
                bottom: 5,
            },
        };
        if (subtitle) {
            output.options.plugins.subtitle = {
                display: ((_q = (_p = output.options.plugins) === null || _p === void 0 ? void 0 : _p.subtitle) === null || _q === void 0 ? void 0 : _q.display) || true,
                text: ((_r = output.options.plugins.subtitle) === null || _r === void 0 ? void 0 : _r.text) || "",
                align: ((_s = output.options.plugins.subtitle) === null || _s === void 0 ? void 0 : _s.align) || "start",
                position: ((_t = output.options.plugins.subtitle) === null || _t === void 0 ? void 0 : _t.position) || "top",
                fullSize: true,
                font: {
                    size: 12,
                    weight: 400,
                },
                padding: ((_u = output.options.plugins.subtitle) === null || _u === void 0 ? void 0 : _u.padding) || {
                    bottom: 10,
                },
            };
        }
        if (["line", "bar", "scatter"].indexOf(output.type) >= 0) {
            output.options.plugins.zoom = {
                pan: {
                    enabled: true,
                    mode: output.type === "scatter" ? "xy" : "x",
                    modifierKey: "shift",
                },
                zoom: {
                    drag: {
                        enabled: true,
                        backgroundColor: "rgba(59, 130, 246, 0.2)",
                        borderColor: "rgb(59, 130, 246)",
                        borderWidth: 2,
                    },
                    pinch: {
                        enabled: true,
                    },
                    mode: output.type === "scatter" ? "xy" : "x",
                    onZoom: () => {
                        setZoomed(true);
                    },
                },
            };
        }
        if (output.type === "matrix") {
            output.data.datasets = output.data.datasets.map((dataset, index) => {
                return Object.assign(Object.assign({}, dataset), { backgroundColor: (context) => {
                        const value = context.dataset.data[context.dataIndex].v;
                        const max = Math.max(...dataset.data.map((d) => d.v));
                        const opacity = (value / max);
                        var alpha = opacity === undefined ? 0.5 : (1 - opacity);
                        return transparentize(dataset.baseColor || "#22c55e", alpha);
                    }, borderWidth: 2, borderColor: theme === "dark" ? "#0f172a" : "#ffffff", width: ({ chart }) => (chart.chartArea || {}).width / chart.scales.x.ticks.length, height: ({ chart }) => (chart.chartArea || {}).height / chart.scales.y.ticks.length });
            });
        }
        output.options.theme = theme;
        return output;
    }, [json, title, theme]);
    return (React.createElement(ErrorBoundary, { fallbackRender: ({ error }) => (React.createElement("div", { className: "onvo-chart-base-error-fallback onvo-flex onvo-h-full onvo-w-full onvo-flex-col onvo-items-center onvo-justify-center" },
            React.createElement(Title, { className: "onvo-error-title" }, "Error rendering chart"),
            React.createElement(Text, { className: "onvo-error-message" }, error.message))) },
        chartConfig ? (chartConfig.type === "table" ? (React.createElement(TableChart, { data: chartConfig })) : (React.createElement("div", { className: "onvo-relative onvo-w-full " +
                (zoomed ? "onvo-h-[calc(100%-40px)]" : "onvo-h-full") },
            React.createElement(Chart, Object.assign({ ref: chartRef, plugins: chartConfig.type === "map" ? [MapPlugin] : [], key: theme }, chartConfig))))) : (React.createElement(React.Fragment, null)),
        React.createElement("div", { className: "onvo-overflow-y-hidden onvo-w-full onvo-mt-1 onvo-px-2 onvo-transition-all onvo-bg-gray-50 dark:onvo-bg-gray-800 onvo-rounded-md onvo-flex onvo-flex-row onvo-items-center onvo-justify-between " +
                (zoomed ? "onvo-h-10" : "onvo-h-0") },
            React.createElement(Text, { className: "onvo-text-xs onvo-font-semibold" },
                "Hold",
                " ",
                React.createElement("span", { className: "onvo-rounded-[4px] onvo-px-1 onvo-py-0.5 onvo-border-solid onvo-border onvo-shadow-sm onvo-border-gray-200 onvo-bg-gray-100 dark:onvo-border-gray-600 dark:onvo-bg-gray-700 dark:onvo-text-gray-400" }, "Shift"),
                " ",
                "and drag to pan"),
            React.createElement(Button, { className: "!onvo-px-2 !onvo-py-0.5 onvo-text-gray-600 dark:onvo-text-gray-300", onClick: resetZoom }, "Reset zoom"))));
};
export default ChartBase;
//# sourceMappingURL=ChartBase.js.map